const PLAYGROUND_THEME_CLASS_PREFIX = 'PlaygroundEditorTheme__';

const CHECKLIST_CLASS_MAP: Record<string, string> = {
  [`${PLAYGROUND_THEME_CLASS_PREFIX}listItemChecked`]:
    'article-checklist-item article-checklist-item--checked',
  [`${PLAYGROUND_THEME_CLASS_PREFIX}listItemUnchecked`]:
    'article-checklist-item article-checklist-item--unchecked',
};

const EDITOR_ONLY_CLASS_PREFIXES = [
  'ImageNode__',
  'editor-',
  'ltr',
  'rtl',
  'nested-listitem',
];

function parseHtml(html: string): Document | null {
  if (typeof DOMParser === 'undefined') return null;
  return new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
}

function shouldStripClass(className: string): boolean {
  if (className.startsWith(PLAYGROUND_THEME_CLASS_PREFIX)) {
    return !CHECKLIST_CLASS_MAP[className];
  }
  return EDITOR_ONLY_CLASS_PREFIXES.some((prefix) => className.startsWith(prefix));
}

function normalizeElementClasses(el: Element): void {
  const raw = el.getAttribute('class');
  if (!raw) return;

  const nextClasses: string[] = [];
  for (const className of raw.split(/\s+/)) {
    if (!className) continue;

    const mapped = CHECKLIST_CLASS_MAP[className];
    if (mapped) {
      nextClasses.push(...mapped.split(/\s+/));
      continue;
    }

    if (shouldStripClass(className)) continue;
    nextClasses.push(className);
  }

  if (nextClasses.length === 0) {
    el.removeAttribute('class');
  } else {
    el.setAttribute('class', [...new Set(nextClasses)].join(' '));
  }
}

function normalizeFigcaptions(doc: Document): void {
  doc.body.querySelectorAll('figcaption').forEach((figcaption) => {
    const text = figcaption.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    figcaption.replaceChildren();
    if (text) {
      figcaption.textContent = text;
    }
  });
}

function convertRoleParagraphDivs(doc: Document): void {
  doc.body.querySelectorAll('[role="paragraph"]').forEach((el) => {
    if (el.tagName !== 'DIV') return;

    const paragraph = doc.createElement('p');
    while (el.firstChild) {
      paragraph.appendChild(el.firstChild);
    }
    el.replaceWith(paragraph);
  });
}

function stripEditorInlineStyles(doc: Document): void {
  doc.body.querySelectorAll('[style]').forEach((el) => {
    const style = el.getAttribute('style');
    if (!style) return;

    const cleaned = style
      .split(';')
      .map((rule) => rule.trim())
      .filter(Boolean)
      .filter((rule) => {
        const [property] = rule.split(':');
        const normalized = property?.trim().toLowerCase();
        return normalized !== 'white-space' && normalized !== 'tab-size';
      })
      .join('; ');

    if (!cleaned) {
      el.removeAttribute('style');
    } else {
      el.setAttribute('style', cleaned);
    }
  });
}

function removeEmptyAttributes(doc: Document): void {
  doc.body.querySelectorAll('[class=""]').forEach((el) => {
    el.removeAttribute('class');
  });
  doc.body.querySelectorAll('[style=""]').forEach((el) => {
    el.removeAttribute('style');
  });
}

/**
 * Lexical playground HTML'ini site `.news-content` ile uyumlu, temiz markup'a çevirir.
 */
export function sanitizeArticleContentHtml(html: string): string {
  if (!html?.trim()) return html;

  const doc = parseHtml(html);
  if (!doc) return html;

  convertRoleParagraphDivs(doc);
  normalizeFigcaptions(doc);
  stripEditorInlineStyles(doc);

  doc.body.querySelectorAll('[class]').forEach((el) => {
    normalizeElementClasses(el);
  });

  removeEmptyAttributes(doc);

  return doc.body.innerHTML;
}
